import React, { useEffect, useState, useCallback } from "react";
import Input from "components/common/input";
import AsyncSelect from "components/common/select/AsyncSelect";
import ToggleButton from "components/common/togglebutton";
import {
  subCategoryInitialValues,
  subCategorySchema,
} from "constants/subCategory";
import { ErrorMessage, Formik } from "formik";
import { getExpenseCategories } from "containers/configuration/api";
import debounce from "lodash/debounce";
const initialMetaForTable = { perPage: 100, page: 1 };

const AddSubCategoryForm = ({
  handleClose,
  handleSubCategorySubmission,
  currentSubCategoryEdit,
}) => {
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchExpenseCategories = async () => {
    setLoading(true);
    try {
      const response = await getExpenseCategories({
        perPage: 1000,
        page: 1,
      });

      if (response?.expense_categories?.length > 0) {
        const activeCategories = response.expense_categories.filter(
          (cat) => cat.status === "active",
        );
        const formattedCategories = activeCategories.map((cat) => ({
          label: cat.name,
          value: cat.id,
        }));
        formattedCategories.sort((a, b) => a.label.localeCompare(b.label));
        setExpenseCategories(formattedCategories);
      } else {
        setExpenseCategories([]);
      }
    } catch (error) {
      console.error("Error fetching expense categories:", error);
      setExpenseCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenseCategories();
  }, []);

  // Prepare initial values for edit mode
  const getInitialValues = () => {
    if (currentSubCategoryEdit) {
      return {
        sub_category_name: currentSubCategoryEdit?.name || "",
        expense_category: currentSubCategoryEdit?.expense_category_id || "",
        account_no: currentSubCategoryEdit?.account_number || "",
        category_status: currentSubCategoryEdit?.status === "active",
      };
    }
    return {
      ...subCategoryInitialValues,
      category_status: true,
    };
  };

  // Prepare data for API submission
  const prepareSubmissionData = (values) => ({
    expense_sub_category: {
      name: values.sub_category_name,
      account_number: values.account_no,
      status: values.category_status ? "active" : "inactive",
      expense_category_id: parseInt(values.expense_category),
    },
  });
  const loadExpenseCategories = useCallback(
    debounce((inputValue, resolve) => {
      getExpenseCategories({ search: inputValue, ...initialMetaForTable })
        .then((res) => {
          const options =
            res?.expense_categories
              ?.filter((cat) => cat.status === "active")
              .map((cat) => ({ label: cat.name, value: cat.id })) || [];
          resolve(options);
        })
        .catch((err) => {
          console.error("Error fetching expense categories:", err);
          resolve([]);
        });
    }, 300),
    [],
  );

  return (
    <Formik
      initialValues={getInitialValues()}
      validationSchema={subCategorySchema}
      enableReinitialize
      onSubmit={(values, { resetForm }) => {
        const submissionData = prepareSubmissionData(values);
        handleSubCategorySubmission(submissionData);
        resetForm();
      }}
    >
      {({ handleChange, values, handleSubmit, setFieldValue }) => (
        <div className="question-modal">
          <form className="form-main" onSubmit={handleSubmit}>
            <hr className="custom-modal-line mt-0" />

            <div>
              <Input
                label="Sub Category Name"
                isRequired
                placeholder="e.g. Office Supplies"
                type="text"
                value={values.sub_category_name}
                name="sub_category_name"
                handleChange={handleChange}
                error={
                  <ErrorMessage
                    className="error-message"
                    component="p"
                    name="sub_category_name"
                  />
                }
              />
            </div>

            <div>
              <AsyncSelect
                isRequired
                label="Expense Category"
                selectedValue={values.expense_category}
                options={expenseCategories}
                placeholder="Select"
                handleChange={(obj) =>
                  setFieldValue("expense_category", obj.value)
                }
                loadOptions={(inputValue) =>
                  new Promise((resolve) =>
                    loadExpenseCategories(inputValue, resolve),
                  )
                }
                error={
                  <ErrorMessage
                    className="error-message"
                    component="p"
                    name="expense_category"
                  />
                }
              />
            </div>

            <div>
              <Input
                label="Account No."
                isRequired
                placeholder="e.g. 5100-001"
                type="text"
                value={values.account_no}
                name="account_no"
                handleChange={handleChange}
                error={
                  <ErrorMessage
                    className="error-message"
                    component="p"
                    name="account_no"
                  />
                }
              />
            </div>

            <div>
              <ToggleButton
                label="Status"
                name="category_status"
                value={values.category_status ? 1 : 0}
                handleChange={(name, value) => {
                  setFieldValue(name, value);
                }}
              />
            </div>

            <div className="action gap-2">
              <button
                className="secondary-btn w-50"
                type="button"
                onClick={handleClose}
              >
                Cancel
              </button>
              <input
                className="primary-btn w-50"
                type="submit"
                name="submit"
                value={
                  currentSubCategoryEdit
                    ? "Edit Sub Category"
                    : "Add Sub Category"
                }
              />
            </div>
          </form>
        </div>
      )}
    </Formik>
  );
};

export default AddSubCategoryForm;
